import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { VideoLastWatch } from "pages/videoPage/home/shared/VideoPlayer";
import { AssetTypes } from "constant/AssetTypes";

const VideoPlayerRedux = createSlice({
  name: "video",
  initialState: {
    currentVideo: null,
    currentLiveTv: null,
    previousVideo: null,
    isFetching: false,
    lastWatched: [],
    error: false,
    channelCategory: "all",
    channelId: 0,
    lockedChannel: null,
    liveTVAdTriggered: false,
    playlistId: 0,
    volume: 0.4,
    // Keyed by creator-video id -> { currentTime, duration }. Fetched once
    // per app session (see App.tsx) so any creator-video card anywhere in
    // the app can show a YouTube-style watch progress bar without each
    // card making its own API call.
    creatorVideoWatchProgress: {},
  },
  reducers: {
    setVideoStart: (state: any) => {
      state.isFetching = true;
    },
    setVideoLoaded: (state: any) => {
      state.isFetching = false;
    },
    setLiveTVSuccess: (state: any, action: any) => {
      state.isFetching = false;
      state.previousVideo = state.currentLiveTv;
      state.currentLiveTv = action.payload;
      state.error = false;
    },
    setVideoSuccess: (state: any, action: any) => {
      state.isFetching = false;
      state.currentVideo = action.payload;
      state.error = false;
    },
    removeVideoData: (state: any) => {
      state.currentVideo = null;
      state.previousVideo = null;
    },
    removeLiveTVData: (state: any) => {
      state.currentLiveTv = null;
    },
    addLastWatch: (state: any, action: any) => {
      // console.log(action.payload);
      // Only TV series groups entries by tv_show_name - a series is
      // tracked as one row shared across all its episodes, so switching
      // episodes within the same show updates that row instead of adding
      // a new one. A movie or a creator video is a single standalone item
      // (unique, stable `id`), so it must match by id+type like Movie
      // always did - creator videos previously fell into the tv_show_name
      // branch by default (the `else`), where `tv_show_name` is always
      // undefined for them, so EVERY creator-video entry matched EVERY
      // OTHER entry lacking a tv_show_name and overwrote it in the .map(),
      // which is what produced duplicate/cloned rows in the pickup list.
      if (
        action.payload.type === "Movie" ||
        action.payload.type === AssetTypes.CreatorVideo
      ) {
        state.lastWatched =
          state.lastWatched &&
          state.lastWatched?.map((d: VideoLastWatch) => {
            if (d.id === action.payload.id && d.type === action.payload.type) {
              return action.payload; // Update existing element
            }
            return d; // Keep the original element
          });
      } else {
        state.lastWatched =
          state.lastWatched &&
          state.lastWatched?.map((d: VideoLastWatch) => {
            if (
              d?.tv_show_name === action.payload.tv_show_name &&
              action.payload.tv_show_name !== undefined
            ) {
              return action.payload; // Update existing element
            }
            return d; // Keep the original element
          });
      }

      // If no match was found, add action.payload to the array
      if (
        state.lastWatched &&
        !state.lastWatched.some(
          (d: VideoLastWatch) =>
            d.id === action.payload.id && d.type === action.payload.type
        )
      ) {
        if (!state.lastWatched) {
          state.lastWatched = [action.payload];
        } else {
          state.lastWatched.push(action.payload);
        }
      }
    },
    removeLastWatch(state: any, action: any) {
      const idToRemove = action.payload;
      const existingItemIndex = state.lastWatched.findIndex(
        (item: any) => item.id === idToRemove
      );

      if (existingItemIndex !== -1) {
        state.lastWatched.splice(existingItemIndex, 1);
      }
    },
    setChannelCategory(state: any, action: any) {
      state.channelCategory = action.payload;
    },
    setCurrentChannelId(state: any, action: any) {
      state.channelId = action.payload;
    },
    setLockedChannel(state: any, action: any) {
      state.lockedChannel = action.payload;
    },
    clearLockedChannel(state: any) {
      state.lockedChannel = null;
    },
    setTriggerLiveTVAd(state: any, action: any) {
      state.liveTVAdTriggered = action.payload;
    },
    setPlaylistID(state: any, action: any) {
      state.playlistId = action.payload;
    },
    // set player volume
    adjustVolumeVideo: (state: any, action: any) => {
      state.volume = action.payload;
    },
    setCreatorVideoWatchProgress: (
      state: any,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.creatorVideoWatchProgress = action.payload || {};
    },
  },
});

export const {
  setVideoStart,
  setLiveTVSuccess,
  setVideoSuccess,
  removeVideoData,
  removeLiveTVData,
  setVideoLoaded,
  addLastWatch,
  setChannelCategory,
  setCurrentChannelId,
  setLockedChannel,
  clearLockedChannel,
  setTriggerLiveTVAd,
  removeLastWatch,
  setPlaylistID,
  adjustVolumeVideo,
  setCreatorVideoWatchProgress,
} = VideoPlayerRedux.actions;
export default VideoPlayerRedux.reducer;
